import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import useFocusTrap from '../../hooks/useFocusTrap';

interface UseDropdownParams {
  onClose?: () => void;
  menuPortalTarget?: HTMLElement | null;
  closeOnOuterScroll?: boolean;
}

interface UseDropdownResult {
  active: boolean;
  setActive: React.Dispatch<React.SetStateAction<boolean>>;
  focusRef: React.RefObject<HTMLDivElement>;
  dropdownRef: React.RefObject<HTMLDivElement>;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export default function useDropdown({
  onClose,
  menuPortalTarget,
  closeOnOuterScroll,
}: UseDropdownParams = {}): UseDropdownResult {
  const [active, setActive] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const focusRef = useFocusTrap<HTMLDivElement>();

  useEffect(() => {
    if (!active) return;

    const handler = (e: MouseEvent | TouchEvent): void => {
      const target = e.target as Node;

      if (dropdownRef.current?.contains(target)) {
        return;
      }

      // When the menu is portaled, clicks on the portal element are outside
      // dropdownRef but should not close the dropdown.
      if (menuPortalTarget && (target as Element).closest?.('.dropdown-list__menu-portal')) {
        return;
      }

      e.stopPropagation();
      if (onClose) onClose();
      setActive(false);
    };

    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [active, onClose, menuPortalTarget]);

  useEffect(() => {
    if (!active || !closeOnOuterScroll) return;

    const scrollHandler = (e: Event): void => {
      const target = e.target as Node;

      if (dropdownRef.current?.contains(target)) return;
      if ((target as Element).closest?.('.dropdown-list__menu-portal')) return;

      if (onClose) onClose();
      setActive(false);
    };

    document.addEventListener('scroll', scrollHandler, true);
    return () => {
      document.removeEventListener('scroll', scrollHandler, true);
    };
  }, [active, onClose, closeOnOuterScroll]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      if (onClose) onClose();
      setActive(false);
    }
  };

  return {
    active,
    setActive,
    focusRef,
    dropdownRef,
    handleKeyDown,
  };
}
